import type { QueryCriteria, ResponseType } from '@/api/types';
import { queryOptions } from '@tanstack/react-query';

import type { CrudEndpoints } from '../endpoints/createCrudEndpoints';

export function createQueryFactory<TCore, TFlat, TEx, TInfo>(
  entityKey: string,
  endpoints: CrudEndpoints<TCore, TFlat, TEx, TInfo>
) {
  return {
    all: () => [entityKey] as const,

    lists: () => [entityKey, 'list'] as const,

    listCore: (criteria?: QueryCriteria) =>
      queryOptions({
        queryKey: [entityKey, 'list', criteria, 'core'] as const,
        queryFn: () => endpoints.getAllCore(criteria)
      }),

    listFlat: (criteria?: QueryCriteria) =>
      queryOptions({
        queryKey: [entityKey, 'list', criteria, 'flat'] as const,
        queryFn: () => endpoints.getAllFlat(criteria)
      }),

    listHierarchy: (criteria?: QueryCriteria) =>
      queryOptions({
        queryKey: [entityKey, 'list', criteria, 'hierarchy'] as const,
        queryFn: () => endpoints.getAllHierarchy(criteria)
      }),

    listInfo: (criteria?: QueryCriteria) =>
      queryOptions({
        queryKey: [entityKey, 'list', criteria, 'info'] as const,
        queryFn: () => endpoints.getAllInfo(criteria)
      }),

    details: () => [entityKey, 'detail'] as const,

    detail: (id: string, resType: ResponseType = 'hierarchy') =>
      queryOptions({
        queryKey: [entityKey, 'detail', id, resType] as const,
        queryFn: () => endpoints.getById(id, resType),
        enabled: !!id
      }),

    detailCore: (id: string) =>
      queryOptions({
        queryKey: [entityKey, 'detail', id, 'core'] as const,
        queryFn: () => endpoints.getCoreById(id),
        enabled: !!id
      }),

    // No-op queries for Suspense hooks when id is undefined (e.g., "new" mode)
    detailNoop: () => ({
      queryKey: [entityKey, 'detail', 'noop'] as const,
      queryFn: () => Promise.resolve(null),
      staleTime: Infinity
    }),

    detailCoreNoop: () => ({
      queryKey: [entityKey, 'detailCore', 'noop'] as const,
      queryFn: () => Promise.resolve(null),
      staleTime: Infinity
    })
  };
}

// Export type helper for consumers
export type QueryFactory<TCore, TFlat, TEx, TInfo> = ReturnType<typeof createQueryFactory<TCore, TFlat, TEx, TInfo>>;
